import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useEmployeeCard, useEmployeeProfile, usePermissions } from '../api/employees';
import { moscowMonth } from '../api/payroll';
import { useEmployeeRegulation } from '../api/regulations';
import { useCurrentUser } from '../api/session';
import { Avatar } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Overlay';
import { formatDate } from '../components/RegulationBlock';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle, usePersonDrawer } from '../layout/shellContext';
import { AttendanceSection, AttendanceToday } from './cabinet/AttendanceSection';
import { CabinetBrief } from './cabinet/BriefSection';
import { FinesSection } from './cabinet/FinesSection';
import { KpiSection } from './cabinet/KpiSection';
import { RegulationSection } from './cabinet/RegulationSection';
import { TasksSection } from './cabinet/TasksSection';

/** Разделы кабинета: Регламент → Оклад и KPI → Нарушения → Посещения → Задачи. */
const SECTIONS = [
  { key: 'regulation', label: 'Регламент' },
  { key: 'kpi', label: 'Оклад и KPI' },
  { key: 'fines', label: 'Нарушения' },
  { key: 'attendance', label: 'Посещения' },
  { key: 'tasks', label: 'Задачи из Bitrix24' },
] as const;

function Rights({ employeeId, onClose }: { employeeId: string; onClose: () => void }) {
  const permissions = usePermissions(employeeId, true);
  return (
    <Modal title="Что вам можно" onClose={onClose}>
      {permissions.isPending ? <LoadingState /> : null}
      {permissions.isError ? <ErrorState error={permissions.error} /> : null}
      <div className="rights">
        {permissions.data?.items.map((item) => (
          <div key={item.action} className="right">
            <span className={`mark ${item.allowed ? 'ok' : 'no'}`}>
              <Icon name={item.allowed ? 'check' : 'x'} size={13} />
            </span>
            <div>
              {item.label}
              {item.allowed ? null : <small className="right-note">{item.reason}</small>}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export function CabinetPage({ own = false }: { own?: boolean }) {
  const params = useParams();
  const me = useCurrentUser();
  const employeeId = own ? (me.data?.employeeId ?? null) : (params.id ?? null);
  const card = useEmployeeCard(employeeId);
  const profile = useEmployeeProfile(employeeId, card.data?.canOpenCabinet ?? false);
  const regulation = useEmployeeRegulation(employeeId, card.data?.canOpenCabinet ?? false);
  const { open } = usePersonDrawer();
  const [rightsOpen, setRightsOpen] = useState(false);
  usePageTitle(own ? 'Мой кабинет' : (card.data?.fullName ?? 'Кабинет'));
  const { hash } = useLocation();
  const loaded = Boolean(card.data);
  // Переход из «Показателей» сразу к разделу: /employees/<id>#tasks.
  useEffect(() => {
    if (loaded && hash) document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: 'start' });
  }, [loaded, hash]);

  if (own && me.data && !me.data.employeeId) return <Navigate to="/org" replace />;
  if (!employeeId || card.isPending) return <LoadingState label="Загружаю кабинет" />;
  if (card.isError) return <ErrorState error={card.error} onRetry={() => void card.refetch()} />;

  const person = card.data;
  return (
    <div className="page">
      <section className="panel profile-head">
        <div className="profile-main">
          <Avatar name={person.fullName} photoUrl={person.photoUrl} size="lg" />
          <div style={{ minWidth: 0 }}>
            <h2>{person.fullName}</h2>
            <p className="person-meta">
              {[person.position, person.departments[0]?.name].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        <dl className="profile-facts">
          <div>
            <dt>{person.managers.length > 1 ? 'Руководители' : 'Руководитель'}</dt>
            <dd>
              {person.managers.length
                ? person.managers.map((manager) => (
                    <div key={manager.id}>
                      <button type="button" className="link" onClick={() => open(manager.id)}>
                        {manager.fullName}
                      </button>
                    </div>
                  ))
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Подчинённые</dt>
            <dd>
              {person.directSubordinates ? (
                <details className="subordinates">
                  <summary>{person.directSubordinates}</summary>
                  <ul>
                    {person.subordinates?.map((subordinate) => (
                      <li key={subordinate.id} className="subordinate-row">
                        <button type="button" className="link" onClick={() => open(subordinate.id)}>
                          {subordinate.fullName}
                        </button>
                        {subordinate.position ? (
                          <small className="subordinate-role">{subordinate.position}</small>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                'нет'
              )}
            </dd>
          </div>
          <div>
            <dt>Город</dt>
            <dd>{person.city ?? '—'}</dd>
          </div>
          {regulation.data ? (
            <div>
              <dt>Регламент</dt>
              <dd>
                {!regulation.data.regulation ? (
                  <span className="status status-neutral">Не заполнен</span>
                ) : regulation.data.signedAt ? (
                  <span className="status status-ok">Подписан {formatDate(regulation.data.signedAt)}</span>
                ) : (
                  <span className="status status-bad">Не подписан</span>
                )}
              </dd>
            </div>
          ) : null}
          {person.canOpenCabinet || person.canReadAttendance ? (
            <AttendanceToday employeeId={person.id} />
          ) : null}
          {profile.data ? (
            <div>
              <dt>Контакты</dt>
              <dd>{[profile.data.mobilePhone, profile.data.email].filter(Boolean).join(' · ') || '—'}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      {regulation.data?.regulation ? (
        <CabinetBrief
          contacts={regulation.data.regulation.contacts ?? []}
          commitments={regulation.data.regulation.kpiCommitments ?? []}
        />
      ) : null}

      <nav className="profile-nav" aria-label="Разделы кабинета">
        {person.canOpenCabinet || person.canReadKpi || person.canReadAttendance || person.canReadPayroll
          ? SECTIONS.filter(
              (section) =>
                person.canOpenCabinet ||
                (section.key === 'kpi' && person.canReadKpi) ||
                (section.key === 'fines' && person.canReadPayroll) ||
                (section.key === 'attendance' && person.canReadAttendance),
            ).map((section) => (
              <a key={section.key} className="pill-toggle" href={`#${section.key}`}>
                {section.label}
              </a>
            ))
          : null}
        <button type="button" className="pill-toggle push" onClick={() => setRightsOpen(true)}>
          <Icon name="shield" size={14} />
          Что мне можно
        </button>
      </nav>

      {person.canOpenCabinet ? (
        <>
          <RegulationSection employeeId={person.id} />
          <KpiSection
            employeeId={person.id}
            commitments={regulation.data?.regulation?.kpiCommitments ?? []}
          />
          <FinesSection employeeId={person.id} currentMonth={moscowMonth()} />
          <AttendanceSection employeeId={person.id} />
          <TasksSection employeeId={person.id} fileName={person.fullName} />
        </>
      ) : person.canReadKpi || person.canReadAttendance || person.canReadPayroll ? (
        // Администратору открыты оклад и KPI, удержания по турникету и посещения;
        // нарушения из файла и остальной кабинет ему не видны.
        <>
          {person.canReadKpi ? <KpiSection employeeId={person.id} /> : null}
          {person.canReadPayroll ? (
            <FinesSection employeeId={person.id} currentMonth={moscowMonth()} withFileFines={false} />
          ) : null}
          {person.canReadAttendance ? <AttendanceSection employeeId={person.id} /> : null}
        </>
      ) : (
        <section className="panel">
          <EmptyState title="Кабинет закрыт">Кабинет видят сам сотрудник, его руководители и HR.</EmptyState>
        </section>
      )}

      {rightsOpen ? <Rights employeeId={person.id} onClose={() => setRightsOpen(false)} /> : null}
    </div>
  );
}
